/**
 * Emulate a device's status bar and gesture bar in a desktop browser (kzd r2 D-7b) — mock and dev builds only.
 *
 * On a phone or tablet the app is drawn edge to edge and `--safe-area-inset-top/-bottom` say how much of the
 * screen the status bar and the gesture bar take (Capacitor's SystemBars publishes them, or app.component's
 * StatusBar fallback). In a desktop browser both are 0px, so a surface that draws under the status bar looks
 * fine there. Open any screen with `?kzInsetTop=24&kzInsetBottom=16` (px, 0–200) and the same variables are set
 * on `<html>`, so every `--ion-safe-area-*` consumer — Ionic's headers and tab bar included — lays out as it
 * would on the device. The values stay for the browser tab's session (`sessionStorage`, a view preference, never
 * business state), so in-app navigation and reloads keep them; `?kzInsetTop=off` clears them.
 *
 * Never on a native platform (the real insets win), and a no-op unless the caller passes `enabled` — main.ts
 * passes `!environment.production`, so stage and prod builds never read the query.
 */
export interface DevSafeArea {
  readonly top: number;
  readonly bottom: number;
}

interface DevSafeAreaWindow {
  readonly location?: { readonly search?: string };
  readonly document?: { readonly documentElement?: { readonly style: Pick<CSSStyleDeclaration, 'setProperty'> } };
  readonly sessionStorage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  readonly Capacitor?: { readonly isNativePlatform?: () => boolean };
}

export const DEV_SAFE_AREA_KEY = 'kz.dev.safeArea';
const MAX_INSET = 200;

function insetOf(raw: string | null): number | null {
  if (raw === null || !/^\d{1,3}$/.test(raw.trim())) {
    return null;
  }
  const n = Number(raw.trim());
  return n <= MAX_INSET ? n : null;
}

/** Applies (and remembers) an emulated inset; returns what is now in effect, or null when nothing is. */
export function applyDevSafeAreaEmulation(
  enabled: boolean,
  win: unknown = typeof window !== 'undefined' ? window : undefined,
): DevSafeArea | null {
  const w = win as DevSafeAreaWindow | undefined;
  const style = w?.document?.documentElement?.style;
  if (!enabled || !style || w?.Capacitor?.isNativePlatform?.() === true) {
    return null;
  }

  let wanted: DevSafeArea | null = null;
  try {
    // Read inside the try: with site data blocked, merely touching `sessionStorage` throws a SecurityError,
    // and this runs before the app boots (D-7b review F1). Then the query still applies, unremembered.
    let store: DevSafeAreaWindow['sessionStorage'];
    try {
      store = w.sessionStorage;
    } catch {
      store = undefined;
    }
    const query = new URLSearchParams(w.location?.search ?? '');
    if (query.has('kzInsetTop') || query.has('kzInsetBottom')) {
      const top = insetOf(query.get('kzInsetTop')) ?? 0;
      const bottom = insetOf(query.get('kzInsetBottom')) ?? 0;
      wanted = top || bottom ? { top, bottom } : null;
      if (wanted) {
        store?.setItem(DEV_SAFE_AREA_KEY, JSON.stringify(wanted));
      } else {
        store?.removeItem(DEV_SAFE_AREA_KEY);
      }
    } else {
      const saved = JSON.parse(store?.getItem(DEV_SAFE_AREA_KEY) ?? 'null') as Partial<DevSafeArea> | null;
      const top = insetOf(String(saved?.top ?? '')) ?? 0;
      const bottom = insetOf(String(saved?.bottom ?? '')) ?? 0;
      wanted = top || bottom ? { top, bottom } : null;
    }
  } catch {
    // A blocked storage or a malformed value: no emulation, the app as a desktop browser draws it.
    wanted = null;
  }

  if (!wanted) {
    return null;
  }
  style.setProperty('--safe-area-inset-top', `${wanted.top}px`);
  style.setProperty('--safe-area-inset-bottom', `${wanted.bottom}px`);
  return wanted;
}
